/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {
    AspectRatio,
    Box,
    Flex,
    Skeleton,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React from 'react'

import {BlogArticleCard} from '../../components/blog-article-card'
import SliderProgress from '../slider-progress/slider-progress'
import ProductScrollerHeader from './product-scroller-header'
import ProductScrollerTile from './product-scroller-tile'
import {useProductScroller} from './use-product-scroller'

/**
 * Renders a scrollable, horizontal container of products with native scroll
 * snapping and manual button controls.
 */
const ProductScroller = ({
    variant = 'product',
    productCarouselContent,
    isLoading,
    scrollProps,
    productTileProps,
    itemWidth = {base: '50%', md: '33.33%', lg: '25%', xl: '24%'},
    showScrollerNav = false,
    maxWidth = 'calc(100vw - 16px)',
    cardsGap,
    scrollContainerId,
    scrollContainerStyles,
    chipProps = {},
    ...props
}) => {
    const products =
        productCarouselContent?.carouselCollection?.items[0]?.itemsCollection
            ?.items
    const carousel = productCarouselContent?.carouselCollection?.items[0]
    const {scroll, scrollRef, totalScrolls, scrollPosition, moveByChip} =
        useProductScroller({
            scrollContainerId,
            items: products,
            itemWidth,
            isLoading
        })
    // Renders nothing if we aren't loading and have no products.
    if ((!products || products?.length < 1) && !isLoading) {
        return (
            <Flex flexDirection="column" gap="20px">
                <Skeleton width={{base: '80%', lg: '30%'}} height="40px" />
                <Flex
                    width={'100%'}
                    display={{base: 'flex', lg: 'none'}}
                    padding="2px"
                    justifyContent="space-evenly"
                    alignItems="flex-start"
                >
                    {[0, 1].map((key) => (
                        <Skeleton key={key} width="40%" height="200px" />
                    ))}
                </Flex>

                <Flex
                    display={{base: 'none', lg: 'flex'}}
                    gap="12px"
                    flexDirection={{base: 'column', lg: 'row'}}
                >
                    {[0, 1, 2, 3].map((key) => (
                        <React.Fragment key={key}>
                            <Box
                                key={key}
                                flex="0 0 auto"
                                width={itemWidth}
                                style={{scrollSnapAlign: 'start'}}
                            >
                                <Stack data-testid="product-scroller-item-skeleton">
                                    <AspectRatio ratio={1}>
                                        <Skeleton />
                                    </AspectRatio>
                                    <Stack spacing={2}>
                                        <Skeleton width="150px" height={5} />
                                        <Skeleton width="75px" height={5} />
                                    </Stack>
                                </Stack>
                            </Box>
                        </React.Fragment>
                    ))}
                </Flex>
            </Flex>
        )
    }

    return (
        <Box position="relative" data-testid="product-scroller" {...props}>
            {
                <>
                    <ProductScrollerHeader
                        title={carousel?.heading}
                        isLoading={isLoading}
                        products={products}
                        scroll={scroll}
                        viewAll={carousel?.cta}
                        showScrollerNav={showScrollerNav}
                    />
                    <Stack
                        spacing={10}
                        pb="0"
                        marginTop={{base: '0', lg: '10px'}}
                        maxW={maxWidth}
                    >
                        <Stack
                            ref={scrollRef}
                            direction="row"
                            gap={cardsGap}
                            spacing={0}
                            wrap="nowrap"
                            overflowX="scroll"
                            {...scrollContainerStyles}
                            py={1}
                            {...scrollProps}
                            sx={{
                                scrollSnapType: 'x mandatory',
                                WebkitOverflowScrolling: 'touch', // Safari touch scrolling needed for scroll snap
                                '-ms-overflow-style': 'none', // Hide scrollbar for IE and Edge
                                'scrollbar-width': 'none', // Hide scrollbar for Firefox
                                '&::-webkit-scrollbar': {
                                    display: 'none' // Hide scrollbar for Chrome, Safari, and Opera
                                },
                                ...scrollProps?.sx
                            }}
                            id={scrollContainerId}
                        >
                            {products.map((product, idx) => (
                                <Box
                                    key={idx}
                                    flex="0 0 auto"
                                    width={itemWidth}
                                    style={{scrollSnapAlign: 'start'}}
                                    id="product-image"
                                >
                                    {variant && variant === 'article' ? (
                                        <BlogArticleCard
                                            data={product}
                                            dynamicImageProps={{
                                                widths: [
                                                    '50vw',
                                                    '50vw',
                                                    '33.33vw',
                                                    '25vw'
                                                ]
                                            }}
                                        />
                                    ) : (
                                        <ProductScrollerTile
                                            data-testid="product-scroller-item"
                                            product={product}
                                            {...(typeof productTileProps ===
                                            'function'
                                                ? {
                                                      ...productTileProps(
                                                          product
                                                      )
                                                  }
                                                : {...productTileProps})}
                                            dynamicImageProps={{
                                                widths: [
                                                    '50vw',
                                                    '50vw',
                                                    '33.33vw',
                                                    '25vw'
                                                ]
                                            }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Stack>
                        {totalScrolls > 1 && showScrollerNav && (
                            <SliderProgress
                                activeIndex={scrollPosition}
                                totalTiles={totalScrolls}
                                moveByChip={moveByChip}
                                {...chipProps}
                            />
                        )}
                    </Stack>
                </>
            }
        </Box>
    )
}

ProductScroller.displayName = 'ProductScroller'

ProductScroller.propTypes = {
    productCarouselContent: PropTypes.shape({
        carouselCollection: PropTypes.shape({
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    heading: PropTypes.string,
                    headingType: PropTypes.oneOf([
                        'h1',
                        'h2',
                        'h3',
                        'h4',
                        'h5',
                        'h6'
                    ]),
                    cta: PropTypes.shape({
                        text: PropTypes.string,
                        url: PropTypes.string,
                        openLinkInNewTab: PropTypes.bool,
                        iconName: PropTypes.string,
                        theme: PropTypes.shape({
                            backgroundColor: PropTypes.string,
                            color: PropTypes.string
                        }),
                        type: PropTypes.string
                    }),
                    itemsCollection: PropTypes.shape({
                        items: PropTypes.arrayOf(
                            PropTypes.shape({
                                productName: PropTypes.string,
                                brand: PropTypes.shape({
                                    name: PropTypes.string
                                }),
                                regularPrice: PropTypes.number,
                                salePrice: PropTypes.number,
                                currency: PropTypes.string,
                                badges: PropTypes.arrayOf(PropTypes.string),
                                image: PropTypes.shape({
                                    desktop: PropTypes.shape({
                                        url: PropTypes.string
                                    })
                                })
                            })
                        )
                    })
                })
            )
        })
    }),
    isLoading: PropTypes.bool,
    showScrollerNav: PropTypes.bool,
    scrollProps: PropTypes.any,
    productTileProps: PropTypes.any,
    itemWidth: PropTypes.object,
    maxWidth: PropTypes.object,
    cardsGap: PropTypes.string,
    scrollContainerId: PropTypes.string,
    scrollContainerStyles: PropTypes.object,
    chipProps: PropTypes.object,
    variant: PropTypes.string
}

export default ProductScroller
